import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { accountStrings, commonStrings } from '../content';
import { hobbyData } from '../content/hobbies';
import { CANCEL_PENDING_SUBSCRIPTION_CHANGE, CANCEL_SUBSCRIPTION, CHANGE_SUBSCRIPTION, CREATE_CHECKOUT_SESSION, GET_MY_SUBSCRIPTION_STATUS, RESUME_SUBSCRIPTION } from '../views/account/operations';
import { getPublicErrorMessage } from '../utils/errors';

const DEFAULT_HOBBIES = hobbyData.map(({ id }) => id);

/**
 * Retrieves plan price.
 *
 * @param {Array<*>} availablePlans - available plans value.
 * @param {*} planKind - plan kind value.
 * @param {*} billingCycle - billing cycle value.
 * @returns {*} The derived operation result.
 */
function getPlanPrice(availablePlans = [], planKind, billingCycle) {
  return availablePlans.find((plan) => plan.planKind === planKind && plan.billingCycle === billingCycle)?.price || '';
}

/**
 * Retrieves plan amount.
 *
 * @param {Array<*>} availablePlans - available plans value.
 * @param {*} planKind - plan kind value.
 * @param {*} billingCycle - billing cycle value.
 * @param {*} fallback - fallback value.
 * @returns {*} The derived operation result.
 */
function getPlanAmount(availablePlans = [], planKind, billingCycle, fallback) {
  const amount = Number(availablePlans.find((plan) => plan.planKind === planKind && plan.billingCycle === billingCycle)?.amount);
  return Number.isFinite(amount) && amount > 0 ? amount : fallback;
}

/**
 * Normalizes usd.
 *
 * @param {number} amount - amount numeric value.
 * @returns {*} The derived operation result.
 */
function formatUsd(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Retrieves annual value.
 *
 * @param {number} monthlyAmount - monthly amount numeric value.
 * @param {number} annualAmount - annual amount numeric value.
 * @param {number} quantity - quantity value.
 * @returns {*} The derived operation result.
 */
function getAnnualValue(monthlyAmount, annualAmount, quantity = 1) {
  const annualizedMonthly = monthlyAmount * 12 * quantity;
  const annualTotal = annualAmount * quantity;
  const savings = annualizedMonthly - annualTotal;
  if (savings <= 0 || annualizedMonthly <= 0) return null;
  return {
    percent: Math.round((savings / annualizedMonthly) * 100),
    effectiveMonthly: annualTotal / 12,
    savings,
  };
}

/**
 * Renders the premium upsell modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.hobbyId - hobby id value.
 * @param {*} props.subscription - subscription value.
 * @param {*} props.onChanged - Callback invoked when changed occurs.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @returns {JSX.Element} The rendered interface.
 */
export default function PremiumUpsellModal({ hobbyId, subscription, onChanged, onClose }) {
  const strings = accountStrings.subscription.upsell;
  const [errorMessage, setErrorMessage] = useState('');
  const isManaging = subscription?.entitlement === 'premium' && ['active', 'trialing'].includes(subscription?.status);
  const initialPlanKind = isManaging ? (subscription.planKind || 'bundle') : (hobbyId ? 'hobby' : 'bundle');
  const subscribedHobbyIds = subscription?.hobbyIds?.length
    ? subscription.hobbyIds
    : (subscription?.entitlements || []).filter((item) => item.entitlement === 'premium').map((item) => item.hobby);
  const initialHobbies = isManaging && subscription.planKind === 'hobby'
    ? Array.from(new Set([...subscribedHobbyIds, ...(hobbyId ? [hobbyId] : [])]))
    : (hobbyId ? [hobbyId] : []);
  const [billingCycle, setBillingCycle] = useState(isManaging ? (subscription.billingCycle || 'monthly') : 'monthly');
  const [planKind, setPlanKind] = useState(initialPlanKind);
  const [selectedHobbies, setSelectedHobbies] = useState(initialHobbies);
  const [createCheckoutSession, { loading }] = useMutation(CREATE_CHECKOUT_SESSION, {
    refetchQueries: [{ query: GET_MY_SUBSCRIPTION_STATUS }],
  });
  const [changeSubscription, { loading: changing }] = useMutation(CHANGE_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_MY_SUBSCRIPTION_STATUS }],
  });
  const [cancelPendingChange, { loading: cancelingPending }] = useMutation(CANCEL_PENDING_SUBSCRIPTION_CHANGE, {
    refetchQueries: [{ query: GET_MY_SUBSCRIPTION_STATUS }],
  });
  const [cancelSubscription, { loading: cancelingPlan }] = useMutation(CANCEL_SUBSCRIPTION, { refetchQueries: [{ query: GET_MY_SUBSCRIPTION_STATUS }] });
  const [resumeSubscription, { loading: resumingPlan }] = useMutation(RESUME_SUBSCRIPTION, { refetchQueries: [{ query: GET_MY_SUBSCRIPTION_STATUS }] });
  const hobbies = DEFAULT_HOBBIES.map((id) => ({ id, label: accountStrings.subscription.hobbyNames[id] || id }));
  const scopedHobbyLabel = hobbyId ? accountStrings.subscription.hobbyNames[hobbyId] || hobbyId : '';
  const availablePlans = subscription?.availablePlans || [];
  const hobbyUnitPrice = getPlanPrice(availablePlans, 'hobby', billingCycle) || (billingCycle === 'annual' ? strings.hobbyUnitAnnualPrice : strings.hobbyUnitPrice);
  const bundlePrice = getPlanPrice(availablePlans, 'bundle', billingCycle) || (billingCycle === 'annual' ? '$79.99 / year' : '$9.99 / month');
  const selectedCount = selectedHobbies.length || (hobbyId ? 1 : 0);
  const canContinue = planKind === 'bundle' || selectedHobbies.length > 0;
  const hobbyMonthlyAmount = getPlanAmount(availablePlans, 'hobby', 'monthly', 3.99);
  const hobbyAnnualAmount = getPlanAmount(availablePlans, 'hobby', 'annual', 34.99);
  const bundleMonthlyAmount = getPlanAmount(availablePlans, 'bundle', 'monthly', 9.99);
  const bundleAnnualAmount = getPlanAmount(availablePlans, 'bundle', 'annual', 79.99);
  const hobbyAmount = billingCycle === 'annual' ? hobbyAnnualAmount : hobbyMonthlyAmount;
  const bundleAmount = billingCycle === 'annual' ? bundleAnnualAmount : bundleMonthlyAmount;
  const hobbyAnnualValue = billingCycle === 'annual' ? getAnnualValue(hobbyMonthlyAmount, hobbyAnnualAmount, Math.max(selectedCount, 1)) : null;
  const bundleAnnualValue = billingCycle === 'annual' ? getAnnualValue(bundleMonthlyAmount, bundleAnnualAmount) : null;
  const bundleIsBetter = planKind === 'hobby' && selectedCount > 0 && hobbyAmount * selectedCount >= bundleAmount;
  const resolvedPlanKind = bundleIsBetter || (planKind === 'hobby' && selectedHobbies.length === 0) ? 'bundle' : planKind;
  const hobbyPrice = selectedCount > 0
    ? `$${(hobbyAmount * selectedCount).toFixed(2)} / ${billingCycle === 'annual' ? 'year' : 'month'}`
    : hobbyUnitPrice;
  const desiredHobbies = resolvedPlanKind === 'hobby' ? [...selectedHobbies].sort() : [];
  const currentHobbies = subscription?.planKind === 'hobby' ? [...subscribedHobbyIds].sort() : [];
  const hasPlanChanges = !isManaging
    || resolvedPlanKind !== subscription?.planKind
    || billingCycle !== subscription?.billingCycle
    || desiredHobbies.join('|') !== currentHobbies.join('|');

  /**
   * Performs hobby.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const toggleHobby = (id) => {
    setPlanKind('hobby');
    setSelectedHobbies((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  /**
   * Handles upgrade.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleUpgrade = async () => {
    setErrorMessage('');
    try {
      const input = {
        billingCycle,
        hobbyIds: resolvedPlanKind === 'hobby' ? selectedHobbies : [],
        planKind: resolvedPlanKind,
      };
      const response = isManaging ? await changeSubscription({ variables: { input } }) : await createCheckoutSession({
        variables: {
          input: {
            ...input,
            platform: 'web',
            planId: billingCycle,
            returnUrl: window.location.href,
          },
        },
      });
      const checkoutUrl = response.data?.createCheckoutSession?.checkoutUrl;
      if (checkoutUrl && checkoutUrl !== window.location.href) {
        window.location.assign(checkoutUrl);
        return;
      }
      await onChanged?.();
      onClose?.();
    } catch (error) {
      setErrorMessage(getPublicErrorMessage(error, accountStrings.subscription.actionFailed));
    }
  };

  /**
   * Handles cancel plan.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleCancelPlan = async () => {
    if (!window.confirm(strings.cancelConfirm)) return;
    setErrorMessage('');
    try {
      await cancelSubscription();
      await onChanged?.();
      onClose?.();
    } catch (error) {
      setErrorMessage(getPublicErrorMessage(error, accountStrings.subscription.actionFailed));
    }
  };

  /**
   * Handles resume plan.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleResumePlan = async () => {
    setErrorMessage('');
    try {
      await resumeSubscription();
      await onChanged?.();
      onClose?.();
    } catch (error) {
      setErrorMessage(getPublicErrorMessage(error, accountStrings.subscription.actionFailed));
    }
  };

  const currentHobbyLabels = subscribedHobbyIds.map((id) => accountStrings.subscription.hobbyNames[id] || id);
  const currentPlanDetail = subscription?.planKind === 'bundle' ? strings.includedHobbies : currentHobbyLabels.join(', ');
  const pendingHobbyLabels = (subscription?.pendingChange?.hobbyIds || []).map((id) => accountStrings.subscription.hobbyNames[id] || id);
  const pendingPlanDetail = subscription?.pendingChange?.planKind === 'bundle' ? strings.includedHobbies : pendingHobbyLabels.join(', ');
  const pendingPlanTitle = subscription?.pendingChange?.planKind === 'bundle' ? strings.bundlePlanTitle : strings.hobbyPlanTitle;

  /**
   * Handles keep current plan.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleKeepCurrentPlan = async () => {
    setErrorMessage('');
    try {
      await cancelPendingChange();
      await onChanged?.();
      onClose?.();
    } catch (error) {
      setErrorMessage(getPublicErrorMessage(error, accountStrings.subscription.actionFailed));
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="modal-content premium-upsell-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-upsell-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="settings-choice-header">
          <div>
            <p className="eyebrow">{accountStrings.subscription.title}</p>
            <h3 id="premium-upsell-title">{isManaging ? strings.manageTitle : hobbyId ? strings.limitTitle(scopedHobbyLabel) : strings.title}</h3>
            <p className="usage-details-copy">{isManaging ? strings.manageSubtitle : hobbyId ? strings.limitSubtitle(scopedHobbyLabel) : strings.subtitle}</p>
          </div>
          <button type="button" className="icon-action" onClick={onClose} aria-label={commonStrings.actions.close}>×</button>
        </div>

        {isManaging && (
          <div className="subscription-benefits-panel compact premium-current-plan">
            <div className="premium-current-plan-summary">
              <div>
                <span className="eyebrow">{strings.currentPlan}</span>
                <h4>{subscription.planKind === 'bundle' ? strings.bundlePlanTitle : strings.hobbyPlanTitle}</h4>
                <p className="usage-details-copy">{currentPlanDetail}</p>
              </div>
              <div className="premium-current-price">
                <strong>{subscription.currentPrice}</strong>
                {subscription.currentPeriodEndsAt && <small>{strings.renews} {new Date(subscription.currentPeriodEndsAt).toLocaleDateString()}</small>}
              </div>
            </div>
            {subscription.pendingChange && (
              <div className="premium-scheduled-change">
                <div>
                  <span className="eyebrow">{strings.scheduledChange}</span>
                  <strong>{pendingPlanTitle}</strong>
                  <small>{pendingPlanDetail}</small>
                  <small>{strings.changesOn(new Date(subscription.pendingChange.effectiveAt).toLocaleDateString())}</small>
                </div>
                <div className="premium-scheduled-change-actions">
                  <strong>{subscription.pendingChange.price}</strong>
                  <button type="button" className="btn-secondary" disabled={cancelingPending} onClick={handleKeepCurrentPlan}>
                    {strings.keepCurrentPlan}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="premium-billing-toggle" role="group" aria-label="Billing cycle">
          {['monthly', 'annual'].map((cycle) => (
            <button
              type="button"
              key={cycle}
              className={billingCycle === cycle ? 'active' : ''}
              onClick={() => setBillingCycle(cycle)}
            >
              {cycle === 'monthly' ? strings.monthly : strings.annual}
            </button>
          ))}
        </div>

        <div className="premium-choice-grid">
          <button
            type="button"
            className={`premium-choice-card ${planKind === 'hobby' ? 'selected' : ''}`}
            onClick={() => setPlanKind('hobby')}
          >
            <span>
              <strong>{strings.hobbyPlanTitle}</strong>
              <small>{selectedCount ? `${selectedCount} selected` : strings.chooseHobbies}</small>
            </span>
            <span className="premium-plan-price">{hobbyPrice}</span>
            {hobbyAnnualValue && (
              <span className="premium-annual-value">
                <em>{strings.savePercent(hobbyAnnualValue.percent)}</em>
                <small>{strings.annualValue(formatUsd(hobbyAnnualValue.effectiveMonthly), formatUsd(hobbyAnnualValue.savings))}</small>
              </span>
            )}
          </button>

          <button
            type="button"
            className={`premium-choice-card popular ${planKind === 'bundle' ? 'selected' : ''}`}
            onClick={() => setPlanKind('bundle')}
          >
            <span>
              <strong>{strings.bundlePlanTitle}<em>{strings.popular}</em></strong>
              <small>{accountStrings.subscription.bundleDetail}</small>
            </span>
            <span className="premium-plan-price">{bundlePrice}</span>
            {bundleAnnualValue && (
              <span className="premium-annual-value">
                <em>{strings.savePercent(bundleAnnualValue.percent)}</em>
                <small>{strings.annualValue(formatUsd(bundleAnnualValue.effectiveMonthly), formatUsd(bundleAnnualValue.savings))}</small>
              </span>
            )}
          </button>
        </div>

        <div className="subscription-benefits-panel compact premium-offer-panel">
          {planKind === 'hobby' ? (
            <>
              <h4>{strings.chooseHobbies}</h4>
              <div className="premium-hobby-picker">
                {hobbies.map((hobby) => (
                  <button
                    type="button"
                    key={hobby.id}
                    className={selectedHobbies.includes(hobby.id) ? 'selected' : ''}
                    onClick={() => toggleHobby(hobby.id)}
                  >
                    {hobby.label}
                  </button>
                ))}
              </div>
              {bundleIsBetter && (
                <p className="premium-value-callout">
                  <span aria-hidden="true">✦</span>
                  {strings.bundleApplied}
                </p>
              )}
            </>
          ) : (
            <p className="usage-details-copy">{strings.includedHobbies}</p>
          )}
          <h4>{accountStrings.subscription.includedTitle}</h4>
          <div className="subscription-benefits">
            {accountStrings.subscription.benefits.map((benefit) => (
              <span key={benefit}>{benefit}</span>
            ))}
          </div>
        </div>

        {errorMessage && <p className="error-copy">{errorMessage}</p>}

        {isManaging && (
          <div className="premium-billing-controls">
            <div>
              <strong>{strings.billingControls}</strong>
              <small>{subscription.cancelAtPeriodEnd ? strings.cancellationScheduled : strings.billingControlsDetail}</small>
            </div>
            {subscription.cancelAtPeriodEnd ? (
              <button type="button" className="btn-secondary" disabled={resumingPlan} onClick={handleResumePlan}>{strings.resumePlan}</button>
            ) : (
              <button type="button" className="btn-secondary danger-soft" disabled={cancelingPlan} onClick={handleCancelPlan}>{strings.cancelPlan}</button>
            )}
          </div>
        )}

        <div className="premium-upsell-actions">
          <button type="button" className="btn-primary" disabled={loading || changing || cancelingPending || cancelingPlan || resumingPlan || !canContinue || !hasPlanChanges} onClick={handleUpgrade}>
            {loading || changing ? accountStrings.messages.saving : bundleIsBetter ? strings.bundlePlanTitle : isManaging ? strings.saveChanges : strings.primaryAction}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders the premium plan selector experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.selectedPlan - selected plan value.
 * @param {*} props.onSelect - Callback invoked when select occurs.
 * @returns {JSX.Element} The rendered interface.
 */
export function PremiumPlanSelector({ selectedPlan, onSelect }) {
  return (
    <div className="premium-plan-selector">
      {Object.entries(accountStrings.subscription.plans).map(([planId, plan]) => (
        <button
          type="button"
          key={planId}
          className={`premium-plan-option ${selectedPlan === planId ? 'selected' : ''}`}
          onClick={() => onSelect(planId)}
        >
          <span>
            <strong>
              {plan.title}
              {plan.badge && <em>{plan.badge}</em>}
            </strong>
            <small>{plan.detail}</small>
          </span>
          <span className="premium-plan-price">{plan.price}</span>
          <span className="premium-plan-check" aria-hidden="true">{selectedPlan === planId ? '✓' : ''}</span>
        </button>
      ))}
    </div>
  );
}
